import type { ComponentType } from "react"

import { isKnownSectionType } from "@/features/feed/contract"
import type { FeedSection, KnownSectionType } from "@/features/feed/contract"
import { BenefitsStripFeedSection } from "@/features/feed/sections/BenefitsStripFeedSection"
import { BentoFeedSection } from "@/features/feed/sections/BentoFeedSection"
import { BrandSpotlightBandFeedSection } from "@/features/feed/sections/BrandSpotlightBandFeedSection"
import { CategoryCoverFeedSection } from "@/features/feed/sections/CategoryCoverFeedSection"
import { CategoryGridFeedSection } from "@/features/feed/sections/CategoryGridFeedSection"
import { CollectionLinksFeedSection } from "@/features/feed/sections/CollectionLinksFeedSection"
import { CtaBandFeedSection } from "@/features/feed/sections/CtaBandFeedSection"
import { DropRailFeedSection } from "@/features/feed/sections/DropRailFeedSection"
import { FaqBandFeedSection } from "@/features/feed/sections/FaqBandFeedSection"
import { GiftsByPriceFeedSection } from "@/features/feed/sections/GiftsByPriceFeedSection"
import { HeroCarouselFeedSection } from "@/features/feed/sections/HeroCarouselFeedSection"
import { HeroPhotoFeedSection } from "@/features/feed/sections/HeroPhotoFeedSection"
import { LookRowFeedSection } from "@/features/feed/sections/LookRowFeedSection"
import { PaymentPlanFeedSection } from "@/features/feed/sections/PaymentPlanFeedSection"
import { PaymentTimelineFeedSection } from "@/features/feed/sections/PaymentTimelineFeedSection"
import { ProductCarouselFeedSection } from "@/features/feed/sections/ProductCarouselFeedSection"
import { ProductListHandoffFeedSection } from "@/features/feed/sections/ProductListHandoffFeedSection"
import { PromoBannerFeedSection } from "@/features/feed/sections/PromoBannerFeedSection"
import { ScaleWallFeedSection } from "@/features/feed/sections/ScaleWallFeedSection"
import { SpotlightFeedSection } from "@/features/feed/sections/SpotlightFeedSection"
import {
  AppPanelFeedSection,
  BentoGridFeedSection,
  BlueProductRailFeedSection,
  BrandLogosFeedSection,
  BrandSpotlightFeedSection,
  CategoryCutoutsFeedSection,
  CategoryHeroFeedSection,
  CategoryPhotosFeedSection,
  CategoryTilesFeedSection,
  CollectionBannersFeedSection,
  CompactProductRailFeedSection,
  CountdownBannerFeedSection,
  CtaPanelFeedSection,
  CustomerStoriesFeedSection,
  FaqFeedSection,
  ImageBannersFeedSection,
  LookCardFeedSection,
  OutfitRowFeedSection,
  PaymentExampleFeedSection,
  PhotoBannerFeedSection,
  PhotoHeroFeedSection,
  PhotoWallFeedSection,
  PlainProductRailFeedSection,
  PriceShortcutsFeedSection,
  ProductRailFeedSection,
  ProductSpotlightFeedSection,
  PromoCardsFeedSection,
  SplitHeroFeedSection,
  StatWallFeedSection,
} from "@/features/feed/sections/StandaloneFeedSections"
import { TestimonialsFeedSection } from "@/features/feed/sections/TestimonialsFeedSection"
import { TrustBandFeedSection } from "@/features/feed/sections/TrustBandFeedSection"
import { TrustPanelFeedSection } from "@/features/feed/sections/TrustPanelFeedSection"
import type { FeedSectionProps } from "@/features/feed/sections/types"

type SectionRegistry = {
  [T in KnownSectionType]: ComponentType<FeedSectionProps<Extract<FeedSection, { type: T }>>>
}

const REGISTRY: SectionRegistry = {
  APP_PANEL: AppPanelFeedSection,
  BENTO_GRID: BentoGridFeedSection,
  BLUE_PRODUCT_RAIL: BlueProductRailFeedSection,
  BRAND_LOGOS: BrandLogosFeedSection,
  BRAND_SPOTLIGHT: BrandSpotlightFeedSection,
  CATEGORY_CUTOUTS: CategoryCutoutsFeedSection,
  CATEGORY_HERO: CategoryHeroFeedSection,
  CATEGORY_PHOTOS: CategoryPhotosFeedSection,
  CATEGORY_TILES: CategoryTilesFeedSection,
  COLLECTION_BANNERS: CollectionBannersFeedSection,
  COMPACT_PRODUCT_RAIL: CompactProductRailFeedSection,
  COUNTDOWN_BANNER: CountdownBannerFeedSection,
  CTA_PANEL: CtaPanelFeedSection,
  CUSTOMER_STORIES: CustomerStoriesFeedSection,
  FAQ: FaqFeedSection,
  IMAGE_BANNERS: ImageBannersFeedSection,
  LOOK_CARD: LookCardFeedSection,
  OUTFIT_ROW: OutfitRowFeedSection,
  PAYMENT_EXAMPLE: PaymentExampleFeedSection,
  PHOTO_BANNER: PhotoBannerFeedSection,
  PHOTO_HERO: PhotoHeroFeedSection,
  PHOTO_WALL: PhotoWallFeedSection,
  PLAIN_PRODUCT_RAIL: PlainProductRailFeedSection,
  PRICE_SHORTCUTS: PriceShortcutsFeedSection,
  PRODUCT_RAIL: ProductRailFeedSection,
  PRODUCT_SPOTLIGHT: ProductSpotlightFeedSection,
  PROMO_CARDS: PromoCardsFeedSection,
  SPLIT_HERO: SplitHeroFeedSection,
  STAT_WALL: StatWallFeedSection,
  HERO_CAROUSEL: HeroCarouselFeedSection,
  HERO_PHOTO: HeroPhotoFeedSection,
  DROP_RAIL: DropRailFeedSection,
  BENTO: BentoFeedSection,
  CATEGORY_COVER: CategoryCoverFeedSection,
  PRODUCT_CAROUSEL: ProductCarouselFeedSection,
  CATEGORY_GRID: CategoryGridFeedSection,
  COLLECTION_LINKS: CollectionLinksFeedSection,
  PROMO_BANNER: PromoBannerFeedSection,
  TRUST_BAND: TrustBandFeedSection,
  TRUST_PANEL: TrustPanelFeedSection,
  BENEFITS_STRIP: BenefitsStripFeedSection,
  PAYMENT_TIMELINE: PaymentTimelineFeedSection,
  TESTIMONIALS: TestimonialsFeedSection,
  BRAND_SPOTLIGHT_BAND: BrandSpotlightBandFeedSection,
  LOOK_ROW: LookRowFeedSection,
  CTA_BAND: CtaBandFeedSection,
  PRODUCT_LIST_HANDOFF: ProductListHandoffFeedSection,
  SPOTLIGHT: SpotlightFeedSection,
  GIFTS_BY_PRICE: GiftsByPriceFeedSection,
  PAYMENT_PLAN: PaymentPlanFeedSection,
  SCALE_WALL: ScaleWallFeedSection,
  FAQ_BAND: FaqBandFeedSection,
}

// An empty shell still reserves cv-section's 600px placeholder and a flow gap.
export const rendersNothing = (type: string): boolean => type === "PRODUCT_LIST_HANDOFF"

export const sectionComponentFor = (type: string): ComponentType<FeedSectionProps> | null => (isKnownSectionType(type) ? (REGISTRY[type] as ComponentType<FeedSectionProps>) : null)
